import { Box } from '@material-ui/core'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import EditIcon from '@material-ui/icons/Edit'
import WarningIcon from '@material-ui/icons/Warning'
import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import {
  Button as RAButton,
  DateField,
  Loading,
  maxValue,
  minValue,
  required,
  TextInput,
  useNotify,
  usePermissions,
  useRefresh,
} from 'react-admin'
import { Form } from 'react-final-form'
import { Grid, List } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { COMPOSITION } from 'shared/helpers/constants'
import { formatCurrency } from 'shared/helpers/formatters'
import { Handler, HandlerOf } from 'shared/helpers/typeHelper'
import {
  bgGray,
  darkGray,
  dividerGray,
  error,
  errorD1,
  kellyGreenD1,
  lightGray1,
  marigoldOrange,
  midGray,
  primaryBlack,
  royalBlue,
  success,
  warning,
} from 'shared/theme/colors'
import { label2 } from 'shared/theme/typography'
import styled from 'styled-components'

import { StatusBadge } from 'components/Common/StatusBadge.styles'
import { captureExceptionIfProduction, convertFileToBase64 } from 'helpers/utils'
import { useUser } from '../../helpers/useUser'
import {
  acceptCreditApplication,
  approveCreditApplication,
  CashSummary,
  createAlloyCreditApplication,
  createPlaidReport,
  EncryptedDocument,
  extendCreditApplication,
  fetchAlloyCreditApplicationProprietaryData,
  fetchAlloyCreditApplications,
  fetchPlaidReports,
  fetchTeamCashSummary,
  PlaidReports,
  PlaidReportStatus,
  rejectCreditApplication,
  resetCreditApplication,
  resetExpiredCreditApplication,
  revealCreditApplicationDocuments,
  uploadCreditApplicationDocuments,
} from '../../models/cash'
import { CreditApplicationStatus } from '../../models/cashSummary'
import { ClickWrapStatus } from '../../models/docusign'
import { CustomBooleanField } from '../Fields'
import TeamPlaidAccountNumber from './TeamPlaidAccountNumber'
import TeamTaxId from './TeamTaxId'

const CreditStatusText = {
  [CreditApplicationStatus.ACCEPTED]: 'Credit extended',
  [CreditApplicationStatus.APPROVED]: 'Terms acceptance pending',
  [CreditApplicationStatus.CANCELED]: 'Cancelled by fleet',
  [CreditApplicationStatus.DECLINED]: 'Offer declined by fleet',
  [CreditApplicationStatus.INELIGIBLE]: 'Ineligible fleet',
  [CreditApplicationStatus.IN_PROGRESS]: 'Application Incomplete',
  [CreditApplicationStatus.IN_REVIEW]: 'Application in review',
  [CreditApplicationStatus.NOT_STARTED]: 'Not applied',
  [CreditApplicationStatus.REJECTED]: 'Credit Rejected',
}

const defaultColors = ['#E3E8EC', midGray]

const CreditStatusColor = {
  [CreditApplicationStatus.ACCEPTED]: [success, kellyGreenD1],
  [CreditApplicationStatus.APPROVED]: [warning, marigoldOrange],
  [CreditApplicationStatus.CANCELED]: [warning, marigoldOrange],
  [CreditApplicationStatus.DECLINED]: [error, errorD1],
  [CreditApplicationStatus.INELIGIBLE]: [error, errorD1],
  [CreditApplicationStatus.IN_PROGRESS]: [error, errorD1],
  [CreditApplicationStatus.IN_REVIEW]: [warning, marigoldOrange],
  [CreditApplicationStatus.NOT_STARTED]: defaultColors,
  [CreditApplicationStatus.REJECTED]: [error, errorD1],
}

const AlloyStatusColor = {
  RUNNING: defaultColors,
  PENDING: defaultColors,
  APPROVED: [success, kellyGreenD1],
  DENIED: [error, errorD1],
  MANUAL_REVIEW: [warning, marigoldOrange],
}

const DocusignStatusColor = {
  [ClickWrapStatus.NOT_STARTED]: defaultColors,
  [ClickWrapStatus.AGREED]: [success, kellyGreenD1],
  [ClickWrapStatus.DECLINED]: [error, errorD1],
  [ClickWrapStatus.INVALID]: [warning, marigoldOrange],
}

type AlloyStatus = 'RUNNING' | 'APPROVED' | 'DENIED' | 'MANUAL_REVIEW' | 'PENDING'

interface AlloyApplication {
  id: string
  status: AlloyStatus
  review_url: string
  person_status: AlloyStatus
  business_status: AlloyStatus
  risk_level?: '1' | '2' | '3'
  credit_limit?: string
  denied_reason?: string
  user: any
}

enum Decision {
  EXTEND = 'EXTEND',
  REJECT = 'REJECT',
}

const Wrap = styled.div`
  padding: 20px;

  pre {
    margin: 0;
    padding: 0;
    font-family: Overpass;
  }

  ${Text} .MuiTypography-body2 {
    font-size: inherit;
    font-family: inherit;
    line-height: inherit;
    font-weight: inherit;
    letter-spacing: inherit;
  }
`

const RevealButton = styled.div`
  ${label2};
  color: ${royalBlue};
  cursor: pointer;
  display: inline-block;
  margin-left: 10px;
  position: relative;
  top: -2px;
`

const StyledEditIcon = styled(EditIcon)`
  font-size: 18px;
  color: ${dividerGray};
  cursor: pointer;
`

const StyledEdit = styled.a`
  ${label2};
  text-decoration: none;
  float: right;
  color: ${royalBlue};
  text-transform: none;
`

const Warning = styled.div`
  margin-top: 10px;
  padding: 5px 10px;
  background-color: ${warning};
  color: ${marigoldOrange};
  display: flex;
  align-items: center;
  gap: 15px;
`

export function TeamCredit(props: any) {
  const { record: team } = props
  const { permissions } = usePermissions()
  const [alloyApplication, setAlloyApplication] = useState<AlloyApplication | null>(null)
  const [cashSummary, setCashSummary] = useState<CashSummary | null>(null)
  const [plaidReports, setPlaidReports] = useState<PlaidReports>({
    lastReport: undefined,
    lastReadyReport: undefined,
  })
  const initFetch = useRef(false)

  useEffect(() => {
    if (team?.id && !initFetch.current) {
      fetchTeamCashSummary(team.id).then(setCashSummary)
      if (cashSummary?.creditApplication) {
        if (cashSummary?.creditApplication?.plaidBankAccount) {
          fetchPlaidReports(team.id, cashSummary.creditApplication.plaidBankAccount).then(setPlaidReports)
        }
        initFetch.current = true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team.id, cashSummary?.creditApplication?.plaidBankAccount])

  useEffect(() => {
    const plaidBankAccount = cashSummary?.creditApplication?.plaidBankAccount
    if (plaidReports?.lastReport?.status === PlaidReportStatus.PRODUCT_NOT_READY && plaidBankAccount) {
      const interval = setInterval(
        () => fetchPlaidReports(team.id, plaidBankAccount).then(setPlaidReports),
        5000
      )
      return () => clearInterval(interval)
    }
  }, [plaidReports, team.id, cashSummary?.creditApplication?.plaidBankAccount])

  const ownerId = team?.members?.find((m: any) => m.id === team.legal_business_owner)?.id
  const { user: owner } = useUser(ownerId)
  const creditStatus = team?.credit_status as CreditApplicationStatus

  if (!cashSummary || !team)
    return (
      <>
        <Box mt={5} />
        <Loading />
        <Box mt={5} />
      </>
    )

  function createReport() {
    if (cashSummary?.creditApplication?.plaidBankAccount) {
      createPlaidReport(team.id, cashSummary.creditApplication.plaidBankAccount).then(setPlaidReports)
    }
  }

  function refetchCashSummary() {
    return fetchTeamCashSummary(team.id).then(setCashSummary)
  }

  return (
    <Wrap>
      <Grid columns="500px 350px" gap="200px">
        <div>
          <Text variant="uppercaseLabel2" fontColor={darkGray}>
            Current
          </Text>
          <Box mt={2} />
          <Grid columns="230px 230px" gap="10px 30px">
            <Text variant="header3" fontColor={lightGray1}>
              Status
            </Text>
            <div>
              <StatusBadge
                $backgroundColor={(CreditStatusColor[creditStatus] || defaultColors)[0]}
                $color={(CreditStatusColor[creditStatus] || defaultColors)[1]}
              >
                {CreditStatusText[creditStatus]}
              </StatusBadge>
            </div>
            {cashSummary?.creditApplication?.publicRejectedReason && (
              <>
                <Text variant="header3" fontColor={lightGray1}>
                  Rejected reason
                </Text>
                <Text variant="label2" fontColor={darkGray}>
                  {cashSummary?.creditApplication?.publicRejectedReason}
                </Text>
              </>
            )}
            {team.date_esign_act_consent && (
              <>
                <Text variant="header3" fontColor={lightGray1}>
                  Submission Date
                </Text>
                <Text variant="label2" fontColor={darkGray}>
                  <DateField
                    record={team}
                    showTime={true}
                    source="date_esign_act_consent"
                    options={{ timeZoneName: 'short' }}
                  />
                </Text>
              </>
            )}
            {cashSummary?.creditApplication?.dateApproved && (
              <>
                <Text variant="header3" fontColor={lightGray1}>
                  Date application approved
                </Text>
                <Text variant="label2" fontColor={darkGray}>
                  <DateField
                    record={cashSummary.creditApplication}
                    showTime={true}
                    source="dateApproved"
                    options={{ timeZoneName: 'short' }}
                  />
                </Text>
              </>
            )}
            {cashSummary?.creditApplication?.dateExpiry && !cashSummary?.creditApplication?.canBeAccepted && (
              <>
                <Text variant="header3" fontColor={lightGray1}>
                  Date application expired
                </Text>
                <Text variant="label2" fontColor={darkGray}>
                  <DateField
                    record={cashSummary.creditApplication}
                    showTime={true}
                    source="dateExpiry"
                    options={{ timeZoneName: 'short' }}
                  />
                </Text>
              </>
            )}
            <Text variant="header3" fontColor={lightGray1}>
              Notes
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              <pre>{cashSummary?.creditApplication?.statusReason || '-'}</pre>
            </Text>
          </Grid>

          {cashSummary?.creditApplication && (
            <>
              <Box mt={4} />
              <Text variant="uppercaseLabel2" fontColor={darkGray}>
                Alloy
              </Text>
              <Box mt={2} />
              <Grid columns="230px 200px" gap="10px 30px">
                <AlloyApplications
                  team={team}
                  cashSummary={cashSummary}
                  setAlloyApplication={setAlloyApplication}
                />
              </Grid>
            </>
          )}

          <Box mt={4} />
          <Text variant="uppercaseLabel2" fontColor={darkGray}>
            Fleet Details
            <StyledEdit href={`/ct-admin/core/team/${team.id}/change/`}>Edit</StyledEdit>
          </Text>
          <Box mt={2} />
          <Grid columns="230px 200px" gap="10px 30px">
            <Text variant="header3" fontColor={lightGray1}>
              Legal name of business
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.legal_name_business}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              Business type
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.business_type}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              State of formation
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.legal_formation_state}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              Business document
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.business_documentation && (
                <a href={team.business_documentation} target="_blank" rel="noreferrer">
                  {getFileName(team.business_documentation)}
                </a>
              )}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              Has authority?
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.composition === COMPOSITION.OOA ? 'YES' : 'NO'}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              MC number
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.mc_number}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              DOT number
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.dot_number}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              Tax ID number
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              <TeamTaxId record={team} permissions={permissions} hideHeader ButtonComponent={RevealButton} />
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              Number of trucks
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.num_power_units}
            </Text>
          </Grid>

          <Box mt={4} />
          <Text variant="uppercaseLabel2" fontColor={darkGray}>
            Applicant
            <StyledEdit href={`/ct-admin/core/customuser/${owner?.id}/change/`}>Edit</StyledEdit>
          </Text>
          <Box mt={2} />
          <Grid columns="230px 200px" gap="10px 30px">
            <Text variant="header3" fontColor={lightGray1}>
              Applicant legal name
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.legal_name_owner || (
                <>
                  {owner?.first_name} {owner?.last_name}
                </>
              )}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              Applicant ID - front
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {owner?.driver?.license_front && (
                <a href={owner.driver.license_front} target="_blank" rel="noreferrer">
                  {getFileName(owner.driver.license_front)}
                </a>
              )}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              Applicant ID - back
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {owner?.driver?.license_back && (
                <a href={owner.driver.license_back} target="_blank" rel="noreferrer">
                  {getFileName(owner.driver.license_back)}
                </a>
              )}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              Owner of the fleet
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.legal_business_owner ? 'Yes' : 'No'}
            </Text>
          </Grid>

          <Box mt={4} />
          <Text variant="uppercaseLabel2" fontColor={darkGray}>
            Debit/credit & Cash flow Accounts
          </Text>
          {cashSummary?.creditApplication?.teamBankAccountHasChanged && (
            <Warning>
              <WarningIcon /> Team bank account has changed.
            </Warning>
          )}
          <Box mt={2} />
          <Grid columns="230px 200px" gap="10px 30px">
            <Text variant="header3" fontColor={lightGray1}>
              Debit/Credit Account
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {cashSummary.creditApplication?.plaidBankAccount?.name}
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              Debit Credit Account #
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              <span>****** {cashSummary.creditApplication?.plaidBankAccount?.mask}</span>
              <Text
                variant="label2"
                as="span"
                fontColor={royalBlue}
                styles="cursor: pointer; padding-left: 23px;"
              >
                <TeamPlaidAccountNumber
                  permissions={permissions}
                  record={team}
                  accountId={cashSummary.creditApplication?.plaidBankAccount?.accountId}
                />
              </Text>
            </Text>
            <Text variant="header3" fontColor={lightGray1}>
              Cash Flow Verification Method
            </Text>
            <Text variant="label2" fontColor={darkGray}>
              {team.plaid_assets_eligible ? 'Plaid' : 'Manual upload'}
            </Text>
            {team.plaid_assets_eligible ? (
              <>
                <PlaidReport
                  team={team}
                  plaidReports={plaidReports}
                  createReport={createReport}
                  canGenerate={cashSummary.creditApplication?.plaidBankAccount?.item.hasAssetsProduct}
                />
                {cashSummary.creditApplication?.plaidBankAccount?.item && (
                  <>
                    <Text variant="header3" fontColor={lightGray1}>
                      Has plaid assets
                    </Text>
                    <Text variant="label2" fontColor={darkGray}>
                      <CustomBooleanField
                        record={{
                          ...cashSummary.creditApplication?.plaidBankAccount.item,
                          id: cashSummary.creditApplication?.plaidBankAccount.item.institutionName,
                        }}
                        source="hasAssetsProduct"
                      />
                    </Text>
                  </>
                )}
              </>
            ) : null}
          </Grid>
          {cashSummary?.creditApplication && (
            <>
              <Box mt={4} />
              <Text variant="uppercaseLabel2" fontColor={darkGray}>
                Additional information
              </Text>
              <Box mt={2} />
              <Grid columns="230px 200px" gap="10px 30px">
                <CreditDocuments team={team} cashSummary={cashSummary} />
                <StyledEdit
                  style={{ float: 'none', fontSize: '14px', marginTop: '10px' }}
                  href={`/ct-admin/cash/creditapplication/?q=${team.id}`}
                >
                  See past applications
                </StyledEdit>
              </Grid>

              <Box mt={4} />
              <DocusignDocuments team={team} cashSummary={cashSummary} />
            </>
          )}
        </div>
        <div>
          <Cta
            team={team}
            refetchCashSummary={refetchCashSummary}
            creditStatus={creditStatus}
            cashSummary={cashSummary}
            alloyApplication={alloyApplication}
          />
        </div>
      </Grid>
    </Wrap>
  )
}

function CreditDocuments({ cashSummary }: { cashSummary: CashSummary; team: any }) {
  const ref = useRef<HTMLInputElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isRevealed, setIsRevealed] = useState(false)
  const [documents, setDocuments] = useState<EncryptedDocument[]>([])
  const creditApplication = cashSummary?.creditApplication
  const creditApplicationId = creditApplication?.id

  async function handleFilesChange(e: React.ChangeEvent<HTMLInputElement>) {
    setIsLoading(true)
    const files = Array.from(e.target?.files || [])
    const filesToStrings = await Promise.all(files.map(convertFileToBase64))
    const attachments = files.map((file, i) => {
      return {
        attachment_file_encrypted: filesToStrings[i],
        original_filename: file.name,
      }
    })

    await uploadCreditApplicationDocuments(creditApplicationId!, { attachments })
    setIsRevealed(true)
    setIsLoading(false)
  }

  useEffect(() => {
    if (isRevealed) {
      revealCreditApplicationDocuments(creditApplicationId!).then((data) => setDocuments(data.attachments))
    }
  }, [creditApplicationId, isRevealed])

  return (
    <>
      {documents.map((document, i) => (
        <React.Fragment key={i}>
          <Text variant="header3" fontColor={lightGray1}>
            {document.original_filename}
          </Text>
          <Text variant="label2" fontColor={darkGray}>
            <a download={document.original_filename} href={document.attachment_file_encrypted}>
              Download
            </a>
          </Text>
        </React.Fragment>
      ))}
      <div>
        <input
          ref={ref}
          accept="image/jpg, image/png, image/gif, .pdf"
          type="file"
          multiple
          onChange={handleFilesChange}
          style={{ display: 'none' }}
        />
        <Button
          margin="15px 0"
          size="medium"
          variant="secondary"
          onClick={() => ref.current?.click()}
          isLoading={isLoading}
        >
          Upload Document
        </Button>
      </div>
      <div>
        {!isRevealed && (creditApplication?.numUploadedDocuments || 0) > 0 && (
          <Text
            variant="label2"
            fontColor={royalBlue}
            styles="cursor: pointer; margin-top: 20px;"
            onClick={() => setIsRevealed(true)}
          >
            Reveal documents ({creditApplication?.numUploadedDocuments})
          </Text>
        )}
      </div>
    </>
  )
}

function PlaidReport({
  team,
  plaidReports,
  createReport,
  canGenerate,
}: {
  team: any
  canGenerate?: boolean
  plaidReports: PlaidReports
  createReport: Handler
}) {
  const lastReportIsPending = plaidReports.lastReport?.status === PlaidReportStatus.PRODUCT_NOT_READY

  function getReadyReport() {
    if (plaidReports.lastReport?.status === PlaidReportStatus.PRODUCT_READY) {
      return plaidReports.lastReport
    }
    return plaidReports.lastReadyReport
  }

  function renderReportUrl() {
    const report = getReadyReport()

    if (report) {
      return (
        <a href={`/api/v1/console/teams/${team.id}/plaid-pdf-report/?report_id=${report.reportId}`} download>
          <DateField
            record={{ ...report, id: report.reportId }}
            source="dateCreated"
            options={{ timeZoneName: 'short' }}
          />
        </a>
      )
    }

    return null
  }

  function renderGenerateOrSpinner() {
    if (!canGenerate) return null
    if (lastReportIsPending) return <InlineSpinner size="1em" />
    return <div onClick={createReport}>Generate&nbsp;report</div>
  }

  return (
    <>
      <Text variant="header3" fontColor={lightGray1}>
        Plaid reports
      </Text>
      <Text variant="label2" fontColor={royalBlue} styles="cursor: pointer">
        <FlexBox alignItems="center" gap="10px">
          {renderReportUrl()}
          {renderGenerateOrSpinner()}
        </FlexBox>
      </Text>
    </>
  )
}

const CtaAmountWrap = styled.div`
  background: ${bgGray};
  padding: 20px;
  border-radius: 7px;
`

function Cta({
  cashSummary,
  refetchCashSummary,
  creditStatus,
  alloyApplication,
}: {
  team: any
  cashSummary: CashSummary
  refetchCashSummary: Handler
  creditStatus: CreditApplicationStatus
  alloyApplication: AlloyApplication | null
}) {
  const [isReseting, setIsReseting] = useState(false)
  const [isResetLoading, setIsResetloading] = useState(false)
  const [isDecisionLoading, setIsDecisionLoading] = useState(false)
  const [isCreditDecionModalOpen, setIsCreditDecionModalOpen] = useState(false)
  const [isExtendingCredit, setIsExtendingCredit] = useState(false)
  const [isExtendingCreditLoading, setIsExtendingCreditLoading] = useState(false)
  const [isResetingExpiredCredit, setIsResetingExpiredCredit] = useState(false)
  const [isResetingExpiredCreditLoading, setIsResetingExpiredCreditLoading] = useState(false)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isAcceptingLoading, setIsAcceptingLoading] = useState(false)
  const refresh = useRefresh()

  async function handleCreditDecision(values: any, decision: Decision) {
    setIsDecisionLoading(true)
    if (decision === Decision.EXTEND) {
      await approveCreditApplication(cashSummary.creditApplication?.id!, values)
    } else if (decision === Decision.REJECT) {
      await rejectCreditApplication(cashSummary.creditApplication?.id!, values)
    }
    await Promise.all([refetchCashSummary(), refresh()])
    setIsDecisionLoading(false)
    setIsCreditDecionModalOpen(false)
  }

  async function handleCreditApplicationReset() {
    setIsResetloading(true)
    await resetCreditApplication(cashSummary.creditApplication?.id!)
    await Promise.all([refetchCashSummary(), refresh()])
    setIsReseting(false)
    setIsResetloading(false)
  }

  async function handleExtendCredit(values: any) {
    setIsExtendingCreditLoading(true)
    await extendCreditApplication(cashSummary.creditApplication?.id!, values)
    await Promise.all([refetchCashSummary(), refresh()])
    setIsExtendingCredit(false)
    setIsExtendingCreditLoading(false)
  }

  async function handleAcceptApplication() {
    setIsAcceptingLoading(true)
    await acceptCreditApplication(cashSummary.creditApplication?.id!)
    await Promise.all([refetchCashSummary(), refresh()])
    setIsAccepting(false)
    setIsAcceptingLoading(false)
  }

  async function handleResetExpiredApplication() {
    setIsResetingExpiredCreditLoading(true)
    await resetExpiredCreditApplication(cashSummary.creditApplication?.id!)
    await Promise.all([refetchCashSummary(), refresh()])
    setIsResetingExpiredCredit(false)
    setIsResetingExpiredCreditLoading(false)
  }

  function renderPrimaryCta() {
    if (creditStatus === CreditApplicationStatus.IN_REVIEW) {
      return (
        <Button size="large" onClick={() => setIsCreditDecionModalOpen(true)} margin="0 auto">
          Make credit decision
        </Button>
      )
    }

    if (
      creditStatus === CreditApplicationStatus.APPROVED ||
      creditStatus === CreditApplicationStatus.ACCEPTED
    ) {
      return (
        <>
          <CtaAmountWrap>
            <Text variant="uppercaseLabel2" fontColor={darkGray}>
              Extended amount
            </Text>
            <FlexBox alignItems="center">
              <Text mt={1} variant="header1">
                {formatCurrency(cashSummary.creditApplication?.amount || 0)}
              </Text>

              <RAButton onClick={() => setIsExtendingCredit(true)}>
                <StyledEditIcon />
              </RAButton>
            </FlexBox>
          </CtaAmountWrap>
          {creditStatus === CreditApplicationStatus.APPROVED && (
            <Button margin="20px auto 0" size="large" onClick={() => setIsAccepting(true)}>
              Accept the application
            </Button>
          )}
          {creditStatus === CreditApplicationStatus.APPROVED &&
            cashSummary?.creditApplication?.dateExpiry &&
            !cashSummary?.creditApplication?.canBeAccepted && (
              <Button margin="20px auto 0" size="large" onClick={() => setIsResetingExpiredCredit(true)}>
                Reset expired application
              </Button>
            )}
        </>
      )
    }
  }

  function renderReset() {
    if (
      [
        CreditApplicationStatus.IN_PROGRESS,
        CreditApplicationStatus.IN_REVIEW,
        CreditApplicationStatus.INELIGIBLE,
        CreditApplicationStatus.CANCELED,
        CreditApplicationStatus.REJECTED,
        CreditApplicationStatus.DECLINED,
      ].includes(creditStatus) &&
      cashSummary.creditApplication?.id
    ) {
      return (
        <Text
          mt={3}
          variant="paragraph1"
          fontColor={royalBlue}
          onClick={() => setIsReseting(true)}
          align="center"
          styles="cursor: pointer;"
        >
          Reset application
        </Text>
      )
    }
  }

  return (
    <>
      <Box mt={4} />
      {renderPrimaryCta()}
      {renderReset()}

      {isCreditDecionModalOpen && (
        <CreditDecisionModal
          isLoading={isDecisionLoading}
          onSuccess={handleCreditDecision}
          onClose={() => setIsCreditDecionModalOpen(false)}
          alloyApplication={alloyApplication}
        />
      )}
      {isReseting && (
        <CreditApplicationResetModal
          isLoading={isResetLoading}
          onSuccess={handleCreditApplicationReset}
          onClose={() => setIsReseting(false)}
        />
      )}
      {isResetingExpiredCredit && (
        <CreditApplicationResetExpiredModal
          isLoading={isResetingExpiredCreditLoading}
          onSuccess={handleResetExpiredApplication}
          onClose={() => setIsResetingExpiredCredit(false)}
        />
      )}
      {isExtendingCredit && (
        <ExtendCreditApplicationModal
          amount={cashSummary.creditApplication?.amount || '0'}
          isLoading={isExtendingCreditLoading}
          onSuccess={handleExtendCredit}
          onClose={() => setIsExtendingCredit(false)}
        />
      )}
      {isAccepting && (
        <CreditApplicationAcceptModal
          isLoading={isAcceptingLoading}
          onSuccess={handleAcceptApplication}
          onClose={() => setIsAccepting(false)}
        />
      )}
    </>
  )
}

function CreditDecisionModal({
  onSuccess,
  onClose,
  isLoading,
  alloyApplication,
}: {
  onSuccess: (values: any, decision: Decision) => void
  onClose: Handler
  isLoading: boolean
  alloyApplication: AlloyApplication | null
}) {
  function getInitialDecision() {
    if (alloyApplication?.status === 'APPROVED') return Decision.EXTEND
    if (alloyApplication?.status === 'DENIED') return Decision.REJECT
    return null
  }
  function getInitialCredit() {
    if (alloyApplication?.status === 'APPROVED' && alloyApplication.credit_limit)
      return alloyApplication.credit_limit
    return null
  }
  function getInitialDeniedReason() {
    if (alloyApplication?.status === 'DENIED' && alloyApplication.denied_reason)
      return alloyApplication.denied_reason
    return null
  }
  const [values, setValues] = useState<any>({
    approved_amount: getInitialCredit(),
    public_rejected_reason: getInitialDeniedReason(),
  })
  const [decision, setDecision] = useState<Decision | null>(getInitialDecision())
  const [confirm, setConfirm] = useState<boolean>(false)

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDecision(event.target.value as Decision)
  }

  function handleConfirm(values: any) {
    setValues(values)
    setConfirm(true)
  }

  function handleSubmit() {
    onSuccess(values, decision!)
  }

  function renderForm(formProps: any) {
    const disableSave = !decision

    if (confirm)
      return (
        <>
          <Text variant="header2" align="left">
            Save the decision and inform the applicant?
          </Text>
          <Box mt={2} />
          <Text variant="paragraph1" fontColor={midGray} align="left">
            An e-mail explaining the decision will be sent to applicant.
          </Text>
          <Box mt={4} />

          <Text variant="header3" fontColor={primaryBlack} align="left">
            Decision:{' '}
            {decision === Decision.EXTEND
              ? `Extend ${formatCurrency(values.approved_amount || 0)}`
              : 'Reject'}
          </Text>
          {decision === Decision.REJECT && (
            <Text variant="header3" fontColor={primaryBlack} align="left" mt={1}>
              Rejection reason: {values.public_rejected_reason}
            </Text>
          )}

          <Box display="flex" flexDirection="column" alignItems="center">
            <Box mt={6} />
            <Button size="large" onClick={handleSubmit} isLoading={isLoading}>
              Continue
            </Button>
            <Box mt={3} />
            <Button variant="text" onClick={() => setConfirm(false)} isDisabled={isLoading}>
              Go back
            </Button>
          </Box>
        </>
      )

    return (
      <>
        <Text variant="header2" align="left">
          Make a credit decision
        </Text>
        <Box mt={4} />

        <RadioGroup row aria-label="decision" value={decision} onChange={handleRadioChange}>
          <FormControlLabel label="Extend" value={Decision.EXTEND} control={<Radio />} />
          <FormControlLabel value={Decision.REJECT} control={<Radio />} label="Reject" />
        </RadioGroup>

        {decision === Decision.EXTEND && (
          <TextInput
            source="approved_amount"
            label="Amount"
            validate={[required(), minValue(1), maxValue(5000)]}
            helperText="Must be between 1 and 5000"
            fullWidth
          />
        )}
        {decision === Decision.REJECT && (
          <>
            <TextInput
              source="public_rejected_reason"
              label="Notes"
              multiline
              fullWidth
              rows={3}
              validate={[required()]}
            />
            <Text variant="paragraph3" fontColor={midGray} align="left" mt={-1}>
              Phrase entered here will be seen by the applicant.
            </Text>
          </>
        )}

        <Box display="flex" flexDirection="column" alignItems="center">
          <Box mt={4} />
          <Button
            type="submit"
            size="large"
            onClick={formProps.handleSubmit}
            isDisabled={disableSave}
            isLoading={isLoading}
          >
            Save decision
          </Button>
          <Box mt={3} />
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </>
    )
  }

  return (
    <SimpleDialog isOpen onClose={onClose}>
      <div style={{ width: '390px' }}>
        <Form
          initialValues={{
            decision: getInitialDecision(),
            approved_amount: getInitialCredit(),
            public_rejected_reason: getInitialDeniedReason(),
          }}
          onSubmit={(values) => (decision ? handleConfirm(values) : undefined)}
          render={renderForm}
        />
      </div>
    </SimpleDialog>
  )
}

function ExtendCreditApplicationModal({
  amount,
  onSuccess,
  onClose,
  isLoading,
}: {
  amount: string | number
  onSuccess: (values: any) => void
  onClose: Handler
  isLoading: boolean
}) {
  const [values, setValues] = useState<any>({})
  const [confirm, setConfirm] = useState<boolean>(false)

  function handleConfirm(values: any) {
    setValues(values)
    setConfirm(true)
  }

  function handleSubmit() {
    onSuccess(values!)
  }

  function renderForm(formProps: any) {
    if (confirm)
      return (
        <>
          <Text variant="header2" align="left">
            Save new credit to the application?
          </Text>
          <Box mt={4} />

          <Text variant="header3" fontColor={primaryBlack} align="left">
            Extend {formatCurrency(values.approved_amount || 0)}
          </Text>

          <Box display="flex" flexDirection="column" alignItems="center">
            <Box mt={6} />
            <Button size="large" onClick={handleSubmit} isLoading={isLoading}>
              Continue
            </Button>
            <Box mt={3} />
            <Button variant="text" onClick={() => setConfirm(false)} isDisabled={isLoading}>
              Go back
            </Button>
          </Box>
        </>
      )

    return (
      <>
        <Text variant="header2" align="left">
          Credit amount
        </Text>
        <Box mt={4} />

        <TextInput
          source="approved_amount"
          label="Amount"
          validate={[required(), minValue(1), maxValue(5000)]}
          fullWidth
        />

        <Box display="flex" flexDirection="column" alignItems="center">
          <Box mt={4} />
          <Button type="submit" size="large" onClick={formProps.handleSubmit} isLoading={isLoading}>
            Save new credit
          </Button>
          <Box mt={3} />
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </>
    )
  }

  return (
    <SimpleDialog isOpen onClose={onClose}>
      <div style={{ width: '390px' }}>
        <Form
          initialValues={{
            approved_amount: amount,
          }}
          onSubmit={handleConfirm}
          render={renderForm}
        />
      </div>
    </SimpleDialog>
  )
}

function CreditApplicationResetModal({
  onSuccess,
  onClose,
  isLoading,
}: {
  onSuccess: Handler
  onClose: Handler
  isLoading: boolean
}) {
  return (
    <SimpleDialog isOpen onClose={onClose}>
      <div style={{ width: '390px' }}>
        <Text variant="header2" align="left">
          Are you sure you want to reset the credit application for this fleet?
        </Text>

        <Text variant="paragraph1" align="left" fontColor={midGray} mt={3}>
          Resetting the application will result in below:
        </Text>

        <Text variant="paragraph1" fontColor={midGray} align="left">
          <List>
            <li>Any existing progress on an incomplete credit application will be removed.</li>
            <li>
              Fleet’s credit status will become “not applied,” An admin will be able to start a new credit
              application.
            </li>
          </List>
        </Text>

        <Box display="flex" flexDirection="column" alignItems="center">
          <Box mt={6} />
          <Button size="large" onClick={onSuccess} isLoading={isLoading}>
            Reset
          </Button>
          <Box mt={3} />
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
        </Box>
      </div>
    </SimpleDialog>
  )
}

function CreditApplicationResetExpiredModal({
  onSuccess,
  onClose,
  isLoading,
}: {
  onSuccess: Handler
  onClose: Handler
  isLoading: boolean
}) {
  return (
    <SimpleDialog isOpen onClose={onClose}>
      <div style={{ width: '390px' }}>
        <Text variant="header2" align="left">
          Reset expired application.
        </Text>

        <Text variant="paragraph1" align="left" fontColor={midGray} mt={3}>
          Resetting expired application will only change approval date set to today date.
        </Text>

        <Box display="flex" flexDirection="column" alignItems="center">
          <Box mt={6} />
          <Button size="large" onClick={onSuccess} isLoading={isLoading}>
            Reset
          </Button>
          <Box mt={3} />
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
        </Box>
      </div>
    </SimpleDialog>
  )
}

function CreditApplicationAcceptModal({
  onSuccess,
  onClose,
  isLoading,
}: {
  onSuccess: Handler
  onClose: Handler
  isLoading: boolean
}) {
  return (
    <SimpleDialog isOpen onClose={onClose}>
      <div style={{ width: '390px' }}>
        <Text variant="header2" align="left">
          Are you sure you want to accept the credit application for this fleet?
        </Text>

        <Box display="flex" flexDirection="column" alignItems="center">
          <Box mt={6} />
          <Button size="large" onClick={onSuccess} isLoading={isLoading}>
            Accept
          </Button>
          <Box mt={3} />
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
        </Box>
      </div>
    </SimpleDialog>
  )
}

function getFileName(file: string) {
  return file.split('/').reverse()[0].split('?')[0]
}

const StyledRow = styled.a`
  text-decoration: none;
  color: inherit;
  display: block;
`

function AlloyApplications({
  cashSummary,
  setAlloyApplication,
}: {
  cashSummary: CashSummary
  team: any
  setAlloyApplication: HandlerOf<AlloyApplication>
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [applications, setApplications] = useState<AlloyApplication[] | null>(null)
  const [proprietaryData, setPropretiaryData] = useState<any>(null)
  const [isProprietaryDataLoading, setIsProprietaryDataLoading] = useState(false)
  const [isProprietaryDataModalOpen, setIsProprietaryDataModalOpen] = useState(false)
  const creditApplicationId = cashSummary?.creditApplication?.id
  const creditApplicationStatus = cashSummary?.creditApplication?.status
  const shouldRefresh = applications?.some((a) => a.status === 'RUNNING' || a.status === 'PENDING')
  const notify = useNotify()

  async function handleNewCreditApplication() {
    setIsLoading(true)
    try {
      await createAlloyCreditApplication(creditApplicationId!)
      await fetchAlloyCreditApplications(creditApplicationId!).then(setApplications)
    } catch (err: any) {
      notify(`Oops! Something went wrong: ${JSON.stringify(err?.response?.data?.message)}`, { type: 'error' })
      captureExceptionIfProduction(err)
    }
    setIsLoading(false)
  }

  async function handleFetchAlloyCreditApplicationProprietaryData() {
    await setIsProprietaryDataLoading(true)
    try {
      await fetchAlloyCreditApplicationProprietaryData(creditApplicationId!).then(setPropretiaryData)
    } catch (err: any) {
      notify(`Oops! Something went wrong: ${JSON.stringify(err?.response?.data?.message)}`, { type: 'error' })
      captureExceptionIfProduction(err)
    }
    setIsProprietaryDataModalOpen(true)
    await setIsProprietaryDataLoading(false)
  }

  useEffect(() => {
    if (creditApplicationId) {
      fetchAlloyCreditApplications(creditApplicationId!).then(setApplications)
    }
  }, [creditApplicationId])

  useEffect(() => {
    if (shouldRefresh) {
      let timeout: NodeJS.Timeout

      const fetch = () => {
        fetchAlloyCreditApplications(creditApplicationId!).then((data) => {
          setApplications(data)
          timeout = setTimeout(fetch, 10000)
        })
      }
      fetch()

      return () => clearTimeout(timeout)
    }
  }, [shouldRefresh, creditApplicationId])

  useEffect(() => {
    if (applications?.length) {
      setAlloyApplication(applications[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications?.length])

  if (!creditApplicationId) return null

  function renderStatus(status: AlloyStatus) {
    return (
      <StatusBadge
        style={{ fontSize: '12px' }}
        $backgroundColor={(AlloyStatusColor[status] || defaultColors)[0]}
        $color={(AlloyStatusColor[status] || defaultColors)[1]}
      >
        {status.replace('_', ' ')}
      </StatusBadge>
    )
  }

  return (
    <>
      {applications?.map((application) => (
        <>
          <Text variant="header3" fontColor={lightGray1}>
            Applicant check
          </Text>
          <Text variant="label2" fontColor={darkGray}>
            {application.person_status ? (
              <StyledRow href={application.review_url} target="_blank" rel="noreferrer" key={application.id}>
                {renderStatus(application.person_status)}
              </StyledRow>
            ) : (
              '-'
            )}
          </Text>

          <Text variant="header3" fontColor={lightGray1}>
            Business check
          </Text>
          <Text variant="label2" fontColor={darkGray}>
            {application.business_status ? (
              <StyledRow href={application.review_url} target="_blank" rel="noreferrer" key={application.id}>
                {renderStatus(application.business_status)}
              </StyledRow>
            ) : (
              '-'
            )}
          </Text>

          {application.credit_limit && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Credit Limit
              </Text>
              <Text variant="label2" fontColor={darkGray}>
                {formatCurrency(String(application.credit_limit))}
              </Text>
            </>
          )}

          {application.risk_level && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Risk level
              </Text>
              <Text variant="label2" fontColor={darkGray}>
                {application.risk_level}
              </Text>
            </>
          )}

          {application.denied_reason && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Denied reason
              </Text>
              <Text variant="label2" fontColor={darkGray}>
                {application.denied_reason}
              </Text>
            </>
          )}

          <Text variant="header3" fontColor={lightGray1}>
            Underwriting
          </Text>
          <Text variant="label2" fontColor={darkGray}>
            <StyledRow href={application.review_url} target="_blank" rel="noreferrer" key={application.id}>
              {renderStatus(application.status)}
            </StyledRow>
          </Text>
        </>
      ))}

      {applications?.length === 0 && creditApplicationStatus === CreditApplicationStatus.IN_REVIEW ? (
        <Button
          margin="15px 0"
          size="medium"
          variant="primary"
          onClick={handleNewCreditApplication}
          isLoading={isLoading}
        >
          Order background check
        </Button>
      ) : null}

      <Button
        margin="15px 0"
        size="medium"
        variant="secondary"
        onClick={handleFetchAlloyCreditApplicationProprietaryData}
        isLoading={isProprietaryDataLoading}
      >
        Show proprietary data
      </Button>

      {isProprietaryDataModalOpen && proprietaryData && (
        <SimpleDialog isOpen onClose={() => setIsProprietaryDataModalOpen(false)}>
          <div style={{ width: '390px' }}>
            <Text variant="header2" align="left">
              Proprietary data:
            </Text>

            <pre style={{ textAlign: 'left' }}>{JSON.stringify(proprietaryData, null, 2)}</pre>
          </div>
        </SimpleDialog>
      )}
    </>
  )
}

function DocusignDocuments({ cashSummary, team }: { cashSummary: CashSummary; team: any }) {
  if (!cashSummary?.creditApplication?.docusignDocuments?.length) return null

  return (
    <>
      <Box mt={4} />
      <Text variant="uppercaseLabel2" fontColor={darkGray}>
        Docusign Documents
      </Text>
      <Box mt={2} />
      {cashSummary?.creditApplication?.docusignDocuments?.map((document) => (
        <Grid
          styles="padding: 8px; margin-left: -8px;"
          columns="180px 1fr"
          gap="10px"
          alignItems="center"
          key={document.docusignId}
        >
          <Text variant="label2" fontColor={darkGray}>
            {document.docusignDocumentName.split(`_${team.id}`)[0]}
          </Text>
          <Text variant="header3" fontColor={lightGray1}>
            <StatusBadge
              style={{ fontSize: '12px' }}
              $backgroundColor={(DocusignStatusColor[document.status] || defaultColors)[0]}
              $color={(DocusignStatusColor[document.status] || defaultColors)[1]}
            >
              {document.status}
            </StatusBadge>
          </Text>
        </Grid>
      ))}
    </>
  )
}
